import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, Sparkles, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

// Medium exposes an RSS feed per user. `rss2json` returns it as JSON with
// CORS enabled — no backend required. Free tier: 10k requests/day.
const MEDIUM_FEED = 'https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@noax-media';
const MEDIUM_PROFILE = 'https://medium.com/@Noax-media';
const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Strip HTML and give a plain-text excerpt (~180 chars) for card previews.
const excerptFromHTML = (html = '', chars = 180) => {
  const text = html
    .replace(/<figure[\s\S]*?<\/figure>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > chars ? `${text.slice(0, chars).trim()}…` : text;
};

// Try common Medium thumbnail sources; return the first that looks like a URL.
const pickCover = (item) => {
  if (item.thumbnail && /^https?:/.test(item.thumbnail)) return item.thumbnail;
  const match = (item.content || item.description || '').match(/<img[^>]+src="([^"]+)"/i);
  if (match && match[1]) return match[1];
  return FALLBACK_COVER;
};

const estimateReadTime = (html = '') => {
  const words = (html.replace(/<[^>]+>/g, ' ').match(/\S+/g) || []).length;
  return Math.max(1, Math.round(words / 220));
};

export default function Blog() {
  useSEO({
    title: 'Blog | Real-Time US Lead Generation Playbooks — Noax Media',
    description: 'Playbooks and field notes on Medicare Leads, Final Expense Leads, and real-time US lead generation from 11+ years in home services and insurance.',
    path: '/blog',
  });
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | error

  useEffect(() => {
    let cancelled = false;
    fetch(`${MEDIUM_FEED}&_cb=${Date.now()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.status !== 'ok' || !Array.isArray(data.items)) {
          throw new Error('Bad feed response');
        }
        const normalized = data.items.map((it, idx) => ({
          id:           it.guid || `medium-${idx}`,
          title:        it.title,
          link:         it.link,
          author:       it.author || 'Noax Media',
          published_at: it.pubDate,
          excerpt:      excerptFromHTML(it.description || it.content),
          cover_image:  pickCover(it),
          categories:   Array.isArray(it.categories) ? it.categories.slice(0, 3) : [],
          read_time:    estimateReadTime(it.content || it.description),
        }));
        setPosts(normalized);
        setStatus('ok');

        // Inject Blog + Article JSON-LD so LinkedIn/Google treat these
        // as professional articles when the blog page is shared.
        try {
          const feedAuthor = data?.feed?.title || 'Noax Media';
          const feedImage  = data?.feed?.image  || 'https://noaxmedia.com/og-image.jpg';
          const ld = {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            'name': 'Noax Media Blog',
            'url': 'https://noaxmedia.com/blog',
            'description': 'Playbooks and field notes on Medicare Leads, Final Expense Leads, and real-time US lead generation.',
            'publisher': {
              '@type': 'Organization',
              'name': 'Noax Media',
              'logo': {
                '@type': 'ImageObject',
                'url': 'https://customer-assets-gfyr7b9c.emergentagent.net/job_php-sample-build/artifacts/ftajnjjx_New%20Noax%20Logo%20-%2003.png',
              },
            },
            'blogPost': normalized.map((p) => ({
              '@type': 'BlogPosting',
              'headline': p.title,
              'description': p.excerpt,
              'url': p.link,
              'mainEntityOfPage': p.link,
              'datePublished': p.published_at,
              'image': p.cover_image || feedImage,
              'author': { '@type': 'Person', 'name': p.author || feedAuthor },
              'publisher': {
                '@type': 'Organization',
                'name': 'Noax Media',
                'logo': {
                  '@type': 'ImageObject',
                  'url': 'https://customer-assets-gfyr7b9c.emergentagent.net/job_php-sample-build/artifacts/ftajnjjx_New%20Noax%20Logo%20-%2003.png',
                },
              },
              'keywords': (p.categories || []).join(', '),
            })),
          };
          let tag = document.getElementById('blog-jsonld');
          if (!tag) {
            tag = document.createElement('script');
            tag.type = 'application/ld+json';
            tag.id = 'blog-jsonld';
            document.head.appendChild(tag);
          }
          tag.textContent = JSON.stringify(ld);
        } catch (_) { /* schema is best-effort */ }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
      const tag = document.getElementById('blog-jsonld');
      if (tag) tag.remove();
    };
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen text-white overflow-hidden relative" data-testid="blog-page">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#31638C] opacity-[0.05] rounded-full blur-3xl animate-drift" />
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.06] rounded-full blur-3xl animate-float-slow" />
      </div>

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-36 lg:pb-20 grain-overlay z-10">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-[#31638C] opacity-[0.12] rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.10] rounded-full blur-3xl animate-drift" />
        <div className="absolute inset-0 grid-pattern opacity-40" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#131E2C]/80 backdrop-blur-sm border border-[#31638C]/20 mb-6">
              <Sparkles size={14} className="text-[#31638C] mr-2" />
              <span className="text-xs font-medium text-[#31638C] tracking-wider uppercase">
                Latest From Medium
              </span>
            </div>
            <h1
              className="text-5xl lg:text-7xl font-bold tracking-tighter leading-none mb-6 text-white"
              data-testid="blog-heading"
            >
              Notes From <br />
              <span className="gradient-text">Operators, Not Marketers.</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
              Playbooks, patterns, and lessons from 11+ years running lead-gen in the trenches of
              home services and insurance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="relative pb-24 lg:pb-32 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {status === 'loading' && (
            <div className="text-center py-20 text-gray-400" data-testid="blog-loading">
              Loading posts from Medium…
            </div>
          )}

          {status === 'error' && (
            <div
              className="max-w-lg mx-auto text-center py-16 px-8 rounded-2xl border border-[#5D676E]/25 bg-[#131E2C]"
              data-testid="blog-error"
            >
              <h3 className="text-xl font-semibold text-white mb-2">
                Could not load posts right now
              </h3>
              <p className="text-gray-400 mb-6 text-sm">
                Follow us directly on Medium to read the latest.
              </p>
              <a
                href={MEDIUM_PROFILE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-[#31638C] to-[#4A8BC2] text-white font-semibold hover:-translate-y-0.5 transition-all"
              >
                Visit our Medium
                <ExternalLink className="ml-2" size={16} />
              </a>
            </div>
          )}

          {status === 'ok' && posts.length === 0 && (
            <div className="text-center py-20 text-gray-400" data-testid="blog-empty">
              No posts published yet — check our Medium soon.
            </div>
          )}

          {status === 'ok' && featured && (
            <motion.div
              className="mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="blog-featured"
            >
              <a
                href={featured.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#131E2C] border border-[#5D676E]/25 rounded-2xl overflow-hidden hover:border-[#31638C]/50 transition-colors shadow-sm"
                data-testid="blog-featured-link"
              >
                <div className="lg:col-span-7 relative h-72 lg:h-[420px] overflow-hidden">
                  <img
                    src={featured.cover_image}
                    alt={`${featured.title} — Noax Media blog on real-time US lead generation for Medicare, final expense, and home services`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    style={{ filter: 'brightness(0.55) contrast(1.1) saturate(0.7)' }}
                    onError={(e) => { e.currentTarget.src = FALLBACK_COVER; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A1420] via-transparent to-transparent" />
                  <div className="absolute top-5 left-5 text-xs uppercase tracking-[0.25em] text-[#7BAFDB] font-semibold bg-[#0A1420]/80 backdrop-blur-md px-3 py-1.5 rounded-full border border-[#31638C]/30">
                    Featured
                  </div>
                </div>
                <div className="lg:col-span-5 p-8 lg:pr-12 lg:pl-2">
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} /> {formatDate(featured.published_at)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={12} /> {featured.read_time} min read
                    </span>
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-white group-hover:text-[#7BAFDB] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-gray-400 leading-relaxed mb-6">{featured.excerpt}</p>
                  <span className="inline-flex items-center text-[#31638C] font-semibold group-hover:text-[#4A8BC2] transition-colors">
                    Read on Medium
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </span>
                </div>
              </a>
            </motion.div>
          )}

          {status === 'ok' && rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, idx) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                >
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col h-full bg-[#131E2C] border border-[#5D676E]/25 rounded-2xl overflow-hidden hover:border-[#31638C]/50 card-hover shadow-sm"
                    data-testid="blog-card"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={`${post.title} — Noax Media insight on Medicare leads, final expense leads, and real-time lead generation`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        style={{ filter: 'brightness(0.55) contrast(1.1) saturate(0.7)' }}
                        onError={(e) => { e.currentTarget.src = FALLBACK_COVER; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#131E2C] via-transparent to-transparent" />
                    </div>
                    <div className="flex flex-col flex-1 p-6">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={12} /> {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={12} /> {post.read_time} min
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#7BAFDB] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed line-clamp-3 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="mt-5 pt-4 border-t border-[#5D676E]/25 flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {(post.categories || []).slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] uppercase tracking-wider text-[#7BAFDB] bg-[#31638C]/10 border border-[#31638C]/30 rounded-full px-2 py-0.5"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <ArrowRight className="text-[#31638C] group-hover:translate-x-1 transition-transform" size={16} />
                      </div>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          )}

          {/* Follow on Medium footer */}
          {status === 'ok' && posts.length > 0 && (
            <div className="mt-16 text-center">
              <a
                href={MEDIUM_PROFILE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#7BAFDB] hover:text-white transition-colors text-sm font-semibold"
              >
                Read more on Medium
                <ExternalLink size={14} />
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
